// Library methods
import { useCallback } from "react";
import { DateTime } from "luxon";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { ExamCardSectionNameType } from "src/constants/enums";

// Types
import { AddExamSectionType, ExamType } from "src/services/exams/types";

// Hooks
import { useAddExam } from "src/services/exams/hooks/useAddExam";

export const useDuplicateExams = () => {
  const { clinicInfo } = useClinicContext();

  const { mutateAsync: addExamMutation } = useAddExam(clinicInfo?.id);

  const getFormattedExamSections = useCallback((examToDuplicate: ExamType) => {
    let examSections: {
      [key in ExamCardSectionNameType]?: Array<AddExamSectionType>;
    } = {};

    const sectionNames = Object.keys(examToDuplicate).filter((key) =>
      key.includes("Sections")
    ) as Array<ExamCardSectionNameType>;

    sectionNames.forEach((sectionName) => {
      examSections[sectionName] = [...(examToDuplicate?.[sectionName] ?? [])];
    });

    return examSections;
  }, []);

  const duplicateExam = useCallback(
    async ({
      examToDuplicate,
      patientId,
    }: {
      examToDuplicate: ExamType;
      patientId: string;
    }) => {
      const today =
        DateTime.now()
          .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
          .startOf("day")
          .toUTC()
          .toISO() ?? "";

      const examSections = getFormattedExamSections(examToDuplicate);
      const examData = {
        scheduledDate: today,
        ...examSections,
      };

      return await addExamMutation({
        patientId,
        examData,
      });
    },
    [getFormattedExamSections, addExamMutation, clinicInfo?.timezone]
  );

  return { duplicateExam };
};
